import { Observable } from 'rxjs/internal/Observable';
import { Injectable } from '@angular/core';
import { RestApiService } from './rest-api.service';
import { MenuViewModel } from '../ViewModel/MenuModel';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ProjectViewModel } from '../ViewModel/ProjectModel';
import { BusinessGroupModel } from '../ViewModel/BusinessGroupModel';
import { UserDetailsViewModel } from '../ViewModel/UserDetailsModel';
import { EncryptDecryptService } from '../Service/encrypt-decrypt.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { IElasticDocModel } from '../ViewModel/ElasticSearch.Model';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  public pageTitle: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public selectedProjectId: BehaviorSubject<number|null> = new BehaviorSubject<number|null>(null);
  public selectedProjectCode: BehaviorSubject<string|null> = new BehaviorSubject<string|null>('');
  public clearImgList: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public head: any = {
    AccessFlag: 'N',
    showSpinner: '1'
  };
  public isAccesingPRAddCommentsPage: Boolean = false;
  public redirectUserToPRAddComments: Boolean = false;
  public redirectUserToPRAddComments_Projectid :string;
  public redirectUserToPRAddComments_ReqNo :string;
  public redirectUserToPRAddComments_GrpID :string;

  constructor(private _restApiService: RestApiService, private encryptDecryptService: EncryptDecryptService,
    private http: HttpClient) {
  }

  private baseUrl = environment.uriHost;

  private uris = {
    getUserMenu: "Menu/GetUserMenu",
    projectBusinessGroupUrl: 'Organization/GetOrganizationAgainstProject',
    projectMemberRoles: 'Member/GetProjectMemberRoles',
    projectMemberFeatures: 'Member/GetProjectMemberFeatures',
    gridSettingsUrl: 'GridSettings/GetGridSettings',
    elasticSearchFilterAll: 'ElasticSearch/FilterAll',
    elasticSearchFilter: 'ElasticSearch/FilterES',
    ProjectMemberLinkGroupDetails: 'Member/GetMemberLinkGroupDetails',
    MemberICs: 'Member/GetMemberICDetails',
  };

  public getSessionStorageItem(item: string): any {
    return localStorage.getItem(item);
  }

  public setSessionStorageItem(key: string, value: any): any {
    this.removeSessionStorageItem(key);
    localStorage.setItem(key, value);
  }

  public removeSessionStorageItem(item: string): any {
    return localStorage.removeItem(item);
  }

  public hasJWT(): boolean {
    return !!localStorage.getItem('currentUserJWT');
  }

  public hasRequestedObject(): boolean {
    return !!localStorage.getItem('requestedObject');
  }

  public hasSearchedObject(): boolean {
    return !!localStorage.getItem('searchedObject');
  }

  public hasLoginAuditNumber(): boolean {
    return !!localStorage.getItem('loginAuditNumber');
  }

  public checkAndDeleteRequestedObject() {
    if (this.hasRequestedObject())
      this.removeSessionStorageItem('requestedObject');
  }

  public checkAndDeleteSearchedObject() {
    if (this.hasSearchedObject())
      this.removeSessionStorageItem('searchedObject');
  }

  public getJWT(): any {
    if (this.hasJWT()) {
      return this.getSessionStorageItem('currentUserJWT');
    }
    else
      return '';
  }

  public getLoginAuditNumber(): string {
    if (this.hasLoginAuditNumber()) {
      return this.getSessionStorageItem('loginAuditNumber');
    }
    else
      return '';
  }

  public setCurrentUserProfileCard(data: UserDetailsViewModel[]|any) {
    let jwtToken = data["jwtToken"];
    let loginAuditNumber = data["loginAuditNumber"];

    if (jwtToken) {
      data["jwtToken"] = '';

      this.removeSessionStorageItem('currentUserJWT');
      this.setSessionStorageItem('currentUserJWT', jwtToken);
    }

    if (loginAuditNumber) {
      data["loginAuditNumber"] = '';

      this.removeSessionStorageItem('loginAuditNumber');
      this.setSessionStorageItem('loginAuditNumber', this.encryptDecryptService.encrypt(loginAuditNumber));
    }

    this.removeSessionStorageItem('profileCard');
    this.setSessionStorageItem('profileCard', this.encryptDecryptService.encrypt(JSON.stringify(data)));
  }

  public getCurrentUserProfileCard(): UserDetailsViewModel[] |any {
    if (this.hasJWT()) {
      let profileCard = this.getSessionStorageItem('profileCard');
      var output = this.encryptDecryptService.decrypt(profileCard);
      var jsonOutput = JSON.parse(output);
      return jsonOutput;
    }
    else
      return null;
  }

  public getLoggedInUserId(): number {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["memberId"];
    }
    else
      return 0;
  }

  public getLoggedInUserEipUserId(): number {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["eipUserId"];
    }
    else
      return 0;
  }

  public getLoggedInUserEmailId(): string {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["eMailId"];
    }
    else
      return '';
  }

  public getLoggedInUserStatus(): boolean {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["isInternal"];
    }
    else
      return false;
  }

  public getLoggedInUserGroupId(): number {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["groupId"];
    }
    else
      return 0;
  }
  /*** To get logged user name */
  public getLoggedInUserName(): string {
    if (this.hasJWT()) {
      let profileCard = this.getCurrentUserProfileCard();
      return profileCard["memberName"];
    }
    else
      return '';
  }

  public getNavigationMenu(projectId: number, userId: number, groupId: number): Observable<MenuViewModel[]> {
    return this._restApiService.new_POST(this.uris.getUserMenu, { projectId: projectId, userId: userId, groupId: groupId });
  }

  public getProjectBusinessGroups(projectId: number): Observable<BusinessGroupModel[]> {
    return this._restApiService.new_POST(this.uris.projectBusinessGroupUrl, { projectId });
  }

  public getProjectMemberLinkGroupDetails(Project_Id: number, Member_Id: number): Observable<any> {
    return this._restApiService.new_POST(this.uris.ProjectMemberLinkGroupDetails, { Project_Id, Member_Id });
  }

  /** Set project code in Session */
  public setProjectCode(projectCode:string, projectId:number, event: ProjectViewModel) {
    this.selectedProjectId.next(projectId);
    this.selectedProjectCode.next(projectCode);
    this.setSessionStorageItem('projectObject', JSON.stringify(event));
    this.setSessionStorageItem('selectedProjectCode', projectCode);
    this.setSessionStorageItem('selectedProjectId', projectId);
  }

  public removeProjectCode() {
    this.selectedProjectCode.next(null);
    this.selectedProjectId.next(null);
    this.removeSessionStorageItem('selectedProjectId');
    this.removeSessionStorageItem('selectedProjectCode');
  }

  /** Get user selected project code */
  public getProjectCode(): string {
    return this.getSessionStorageItem('selectedProjectCode');
  }

  /** Get user selected project id */
  public getProjectId(): number {
    return Number(this.getSessionStorageItem('selectedProjectId'));
  }

  public getCurrentProjectDetails(): ProjectViewModel {
    let projModel: ProjectViewModel = JSON.parse(this.getSessionStorageItem('projectObject'));
    return projModel;
  }

  public isProjectCodeSelected(): boolean {
    return !!this.getProjectCode();
  }

  public GetGridSettings(postData: any) {
    return this.http.post<any>(this.uris.gridSettingsUrl, postData.requestedparamsforgetdatafromDB, { headers: postData.headers });
    // return this._restApiService.new_POST(this.uris.gridSettingsUrl, postData.requestedparamsforgetdatafromDB);
  }

  /**
    * Get Project Member Roles API
    * @param ProjecId,@param GroupId,@param MemberId
    */
  public getProjectMemberRoles(projectId?: number|null, groupId?: number|null, memberId?: number, icId?: number|null): Observable<any> {
    return this._restApiService.new_POST(this.uris.projectMemberRoles, { Project_Id: projectId, Group_Id: groupId, Member_Id: memberId, IC_Id: icId });
  }

  public getMemberICs(memberId?: number): Observable<any> {
    return this._restApiService.new_POST(this.uris.MemberICs, { Member_Id: memberId });
  }

  /**
    * Get Project Member Features API
    * @param ProjecId,@param GroupId,@param MemberId
    */
  public getProjectMemberFeatures(projectId?: number|null, memberId?: number, groupId?: number|null, icId?: number|null): Observable<any> {
    return this._restApiService.new_POST(this.uris.projectMemberFeatures, { Project_Id: projectId, Member_Id: memberId, Group_Id: groupId, IC_Id: icId });
  }

  public elasticSearchFilterAll(_projectId:number, _index:number): Observable<any[]> {
    return this._restApiService.new_POST(this.uris.elasticSearchFilterAll, { projectId: _projectId, index: _index });
  }

  public elasticSearchFilter(_searchText:any): Observable<any[]> {
    return this._restApiService.new_POST(this.uris.elasticSearchFilter, { searchText: _searchText, memberId: this.getLoggedInUserId() })
      .pipe(map((res: any[]) => {
        return res.map(x => {
          let moduleName: string = '';
          switch (x.moduleId) {
            case 1:
              moduleName = 'Repository';
              break;
            case 2:
              moduleName = 'DCI';
              break;
            case 3:
              moduleName = 'Mail'
              break;
          }
          let displayText: string = moduleName + ' - ' + x.recordNumber + ' - ' + x.title;
          if (!!x.revisionNo) {
            displayText = displayText + ' - ' + x.revisionNo;
          }
          let esresult: IElasticDocModel = {
            projectId: x.projectId, moduleId: x.moduleId, moduleName: moduleName, recordId: x.recordId,
            recordNumber: x.recordNumber, groupId: x.groupId, title: x.title, revisionNo: x.revisionNo, displayText: displayText
          };
          return esresult;
        });
      }));
  }

}
